import { useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { Card, Button } from 'react-bootstrap';
import { Upload, FileSpreadsheet, X } from 'lucide-react';
import type { ParsedMultiXlsx } from '../../../../apps/hooks/oms/useOrderSegment';

interface XlsxUploaderProps {
  onParse: (files: File[]) => Promise<void>;
  parsedResult: ParsedMultiXlsx | null;
  parseError: string | null;
  parsing: boolean;
  onClear: () => void;
}

const XlsxUploader = ({ onParse, parsedResult, parseError, parsing, onClear }: XlsxUploaderProps) => {
  const onDrop = useCallback((acceptedFiles: File[]) => {
    if (acceptedFiles.length > 0) {
      onParse(acceptedFiles);
    }
  }, [onParse]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
    },
    disabled: parsing,
  });

  if (parsedResult) {
    return (
      <Card>
        <Card.Body>
          <div className="d-flex justify-content-between align-items-start">
            <div style={{ flex: 1 }}>
              {parsedResult.files.map((file, i) => (
                <div key={i} className={i > 0 ? 'mt-2 pt-2 border-top' : ''}>
                  <div className="d-flex align-items-center gap-2">
                    <FileSpreadsheet size={16} className="text-success" />
                    <strong style={{ fontSize: '0.875rem' }}>{file.fileName}</strong>
                    <small className="text-muted">
                      {file.orderIds.length} IDs from {file.totalRows} rows
                    </small>
                  </div>
                </div>
              ))}
              <div className="mt-2 pt-2 border-top">
                <strong>{parsedResult.mergedOrderIds.length}</strong> unique order IDs total
                {parsedResult.files.length > 1 && (
                  <small className="text-muted ms-1">
                    (from {parsedResult.files.length} files)
                  </small>
                )}
              </div>
              <div className="mt-2">
                <small className="text-muted">Preview (first 5):</small>
                <div className="mt-1">
                  {parsedResult.mergedOrderIds.slice(0, 5).map((id) => (
                    <code key={id} className="d-block" style={{ fontSize: '0.8rem' }}>
                      {id}
                    </code>
                  ))}
                  {parsedResult.mergedOrderIds.length > 5 && (
                    <small className="text-muted">...and {parsedResult.mergedOrderIds.length - 5} more</small>
                  )}
                </div>
              </div>
            </div>
            <Button variant="outline-secondary" size="sm" onClick={onClear}>
              <X size={16} />
            </Button>
          </div>
        </Card.Body>
      </Card>
    );
  }

  return (
    <>
      <Card
        {...getRootProps()}
        style={{
          cursor: parsing ? 'wait' : 'pointer',
          border: isDragActive ? '2px dashed #0d6efd' : '2px dashed #dee2e6',
          backgroundColor: isDragActive ? '#f0f7ff' : undefined,
        }}
      >
        <Card.Body className="text-center py-4">
          <input {...getInputProps()} />
          {parsing && <p className="mb-0 text-muted">Parsing files...</p>}
          {!parsing && isDragActive && <p className="mb-0 text-primary">Drop XLSX files here</p>}
          {!parsing && !isDragActive && (
            <>
              <Upload size={32} className="text-muted mb-2" />
              <p className="mb-0 text-muted">
                Drag & drop XLSX files here, or click to select
              </p>
              <small className="text-muted">Accepts .xlsx files — multiple files supported</small>
            </>
          )}
        </Card.Body>
      </Card>
      {parseError && (
        <div className="text-danger mt-2" style={{ fontSize: '0.875rem' }}>
          {parseError}
        </div>
      )}
    </>
  );
};

export default XlsxUploader;
